import { Worker, type Job } from "bullmq";
import { bullmqConnectionConfig } from "../config/bullmq.js";
import { env } from "../config/env.js";
import { prisma } from "../config/prisma.js";

export interface WhatsAppJobData {
  to: string;
  message: string;
  messageId: string;
  templateName?: string;
  templateLanguage?: string;
}

export function startWhatsAppWorker(
  prismaClient: typeof prisma,
  logger: {
    info: (msg: string) => void;
    error: (msg: string) => void;
    warn: (msg: string) => void;
  },
) {
  const worker = new Worker<WhatsAppJobData>(
    "whatsapp",
    async (job: Job<WhatsAppJobData>) => {
      const { to, message, messageId, templateName, templateLanguage } =
        job.data;
      logger.info(
        `[WhatsApp Worker] Processing job ${job.id} for message ${messageId}`,
      );

      const accessToken = env.WHATSAPP_ACCESS_TOKEN;
      // const phoneNumberId = env.WHATSAPP_PHONE_NUMBER;

      const url = env.WHATSAPP_API_URL;
      // const url = `https://graph.facebook.com/v25.0/${phoneNumberId}/messages`;
      const payload = templateName
        ? {
            messaging_product: "whatsapp",
            to,
            type: "template",
            template: {
              name: templateName,
              language: { code: templateLanguage || "en_US" },
            },
          }
        : {
            messaging_product: "whatsapp",
            recipient_type: "individual",
            to,
            type: "text",
            text: { body: message },
          };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const responseData = (await response.json()) as any;

        if (!response.ok) {
          throw new Error(
            `WhatsApp API Error (${response.status}): ${
              responseData?.error?.message || JSON.stringify(responseData)
            }`,
          );
        }

        await prismaClient.message.update({
          where: { id: messageId },
          data: { status: "success" },
        });

        logger.info(
          `[WhatsApp Worker] Message ${messageId} sent. Meta ID: ${responseData?.messages?.[0]?.id}`,
        );
      } catch (err: any) {
        await prismaClient.message.update({
          where: { id: messageId },
          data: { status: "failed" },
        });

        logger.error(
          `[WhatsApp Worker] Job failed for message ${messageId}: ${err.message}`,
        );
        throw err;
      }
    },
    {
      connection: bullmqConnectionConfig,
      concurrency: 5,
    },
  );

  worker.on("failed", (job, err) => {
    logger.error(
      `[WhatsApp Worker] Job ${job?.id} permanently failed: ${err.message}`,
    );
  });

  worker.on("error", (err) => {
    logger.error(`[WhatsApp Worker] Runtime Error: ${err.message}`);
  });

  return {
    worker,
    close: async () => {
      await worker.close();
    },
  };
}
